import { ShieldCheckIcon, ShieldIcon, UserRoundIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ACCESS_LEVELS, LEVEL_DESCRIPTION, LEVEL_LABEL } from '@/lib/roles';
import type { AccessLevel } from '@/lib/types';
import { cn } from '@/lib/utils';

const NOTE: Record<AccessLevel, string> = { super_user: 'Organization-wide', pm_admin: 'Can send texts', pm_user: 'Cannot send texts' };
const ICON: Record<AccessLevel, typeof ShieldIcon> = { super_user: ShieldCheckIcon, pm_admin: ShieldIcon, pm_user: UserRoundIcon };

/**
 * The three access levels as a band of tiles: what each one can do and how many people hold it. Explains the
 * model on the Users and Team pages without a paragraph.
 */
export function AccessLevelTiles({ counts, className, onPick, active }: { counts: Record<AccessLevel, number> | undefined; className?: string; onPick?: (level: AccessLevel) => void; active?: AccessLevel | null }) {
  return (
    <div className={cn('bg-border grid gap-px border sm:grid-cols-3', className)} role="list" aria-label="Access levels">
      {ACCESS_LEVELS.map((level) => {
        const Icon = ICON[level];
        const n = counts?.[level];
        const body = (
          <>
            <span className="bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center">
              <Icon className="size-4" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium">{LEVEL_LABEL[level]}</span>
                <span className={cn('font-display text-2xl leading-none tracking-tight tabular-nums', n === 0 && 'text-muted-foreground/60')}>{n === undefined ? <Skeleton className="inline-block h-6 w-6" /> : n}</span>
              </span>
              <span className="text-muted-foreground mt-1 block text-xs leading-snug">
                <span className="text-foreground/80">{NOTE[level]}.</span> {LEVEL_DESCRIPTION[level]}
              </span>
            </span>
          </>
        );
        const cls = cn('bg-card flex min-w-0 items-start gap-3 px-4 py-3.5 text-left sm:px-5', onPick && 'hover:bg-accent/40 transition-colors', active === level && 'bg-accent/50');
        return onPick ? (
          <button key={level} type="button" role="listitem" aria-pressed={active === level} onClick={() => onPick(level)} className={cls} title={`Show ${LEVEL_LABEL[level]}s`}>
            {body}
          </button>
        ) : (
          <div key={level} role="listitem" className={cls}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
